import { Dispatch, useCallback, useEffect, useRef, useState } from 'react';
import { ActionIcon, Button, Group, Popover, TextInput } from '@mantine/core';
import { BiFilterAlt as FilterIcon } from 'react-icons/bi';
import { useIntlLight } from '../../../../../i18n';
import { actionTypes } from '../../../../types';
import useFilterStyles from './useFilterStyles';

export default function StringFilter({
  columnName,
  state,
  dispatch,
}: {
  columnName: string;
  state: any;
  dispatch: Dispatch<any>;
}) {
  const { t } = useIntlLight();
  const [opened, setOpened] = useState(false);
  const filter = (state?.filters || []).find((_filter: any) => _filter.property === columnName) || {
    value: '',
  };
  const { classes } = useFilterStyles({ isFilterEnabled: Boolean(filter?.value) });
  const inputRef = useRef<HTMLInputElement>(null);

  const handleClose = () => {
    setOpened(false);
  };

  const handleClear = () => {
    dispatch({ type: actionTypes.SET_FILTER, payload: { property: columnName, value: '' } });
    setOpened(false);
  };

  const handleApply = useCallback(() => {
    dispatch({
      type: actionTypes.SET_FILTER,
      payload: { property: columnName, value: inputRef?.current?.value || '' },
    });
    setOpened(false);
  }, [dispatch, columnName]);

  useEffect(() => {
    if (opened) {
      inputRef?.current?.focus();
      inputRef?.current?.addEventListener('keyup', event => {
        if (event.key !== 'Enter') return;
        handleApply();
      });
    }
  }, [opened, inputRef, handleApply]);

  return (
    <Popover
      opened={opened}
      onClose={handleClose}
      position="bottom"
      transition="scale-y"
      withinPortal
    >
      <Popover.Target>
        <ActionIcon
          size="sm"
          className={classes.filterIconButton}
          onClick={() => setOpened(o => !o)}
          color={filter?.value ? 'blue' : 'gray'}
          variant="filled"
        >
          <FilterIcon />
        </ActionIcon>
      </Popover.Target>
      {opened && (
        <Popover.Dropdown>
          <TextInput
            ref={inputRef}
            placeholder={t('app.enterValue')}
            mb="sm"
            defaultValue={filter.value}
            autoComplete="off"
          />
          <Group position="apart">
            <Button color="red" onClick={handleClear} disabled={!filter.value}>
              {t('app.clear')}
            </Button>
            <Button onClick={handleApply}>{t('app.apply')}</Button>
          </Group>
        </Popover.Dropdown>
      )}
    </Popover>
  );
}
